import { BLOCKS } from "#lib/app_config.js";
import { tick } from "svelte";
import { nanoid } from "#lib/util.js";
import { prepare_blocks_for_insert } from "#lib/block_clipboard.js";

export class ContainerModel {
  blocks = $state([]);
  block_types = $state([]);
  default_block_type = $state('text');

  // Two types of selections are possible:
  // ContainerSelection:
  // {
  //   type: 'container',
  //   block_indexes: [3, 4, 5],
  //   block_anchor_index: 3,
  // }
  // Field selection (text selection inside a block)
  // {
  //   type: 'field',
  //   block_index: 3,
  //   field_name: 'title',
  //   char_anchor_index: 3, // maybe later (currently not reliable)
  //   char_focus_index: 5, // maybe later (currently not reliable)
  // }
  selection = $state(undefined);
  
  // TODO: this should respect offset (multi-selections)
  selected_index = $derived(this.selection?.block_indexes?.length === 1 ? this.selection.block_indexes[0] : this.selection?.block_index);
  
  // Undefined if more than 1 blocks are selected
  selected_block = $derived(this.blocks[this.selected_index]);
  
  // All selected blocks (works for both single and multi-selection)
  selected_blocks = $derived(() => {
    if (!this.selection) return [];
    if (this.selection.type === 'container' && this.selection.block_indexes?.length > 0) {
      return this.selection.block_indexes.map(i => this.blocks[i]).filter(Boolean);
    }
    if (this.selection.type === 'field' && this.selection.block_index !== undefined) {
      const block = this.blocks[this.selection.block_index];
      return block ? [block] : [];
    }
    return [];
  });
  
  // Tracks which selected block is currently hovered (for toolbar positioning in multi-selection)
  hovered_selected_index = $state(null);
  
  // The block index that should show the toolbar (hovered block if valid, otherwise anchor)
  toolbar_block_index = $derived(() => {
    if (!this.selection || this.selection.type !== 'container') return null;
    const indexes = this.selection.block_indexes;
    if (!indexes || indexes.length === 0) return null;
    // Single selection: always show on that block
    if (indexes.length === 1) return indexes[0];
    // Multi-selection: prefer hovered block if it's selected, otherwise use anchor
    if (this.hovered_selected_index !== null && indexes.includes(this.hovered_selected_index)) {
      return this.hovered_selected_index;
    }
    return this.selection.block_anchor_index;
  });

  // UI-related
  ref = $state(); // this will be the link to the Container.svelte component instance 

  constructor({ blocks, block_types, default_block_type }, parent) {
    this.parent = parent;

    // FIX: ensure every block has a unique block_id.
    // Blocks written outside the editor (imports, seeds, server-side writes) can lack a
    // block_id entirely. Every missing id reads as `undefined`, so a set of them collapses
    // to a single value — which is why this used to report "duplicate block_ids" when the
    // blocks in fact had none. We only mint ids for blocks that are missing one or that
    // collide, so ids that are already valid stay stable (block_id is used as the DOM id
    // and as the {#each} key, and sticker anchor names are derived from it).
    const seen_block_ids = new Set();
    let repaired_block_count = 0;

    blocks = blocks.map(block => {
      if (block.block_id && !seen_block_ids.has(block.block_id)) {
        seen_block_ids.add(block.block_id);
        return block;
      }
      let block_id = nanoid();
      while (seen_block_ids.has(block_id)) block_id = nanoid();
      seen_block_ids.add(block_id);
      repaired_block_count++;
      return { ...block, block_id };
    });

    if (repaired_block_count > 0) {
      console.warn(
        `Assigned a new block_id to ${repaired_block_count} block(s) that were missing one or duplicated another. ` +
        `Save the entry to persist the fix.`
      );
    }

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      if (BLOCKS[block.type]) {
        const ModelClass = BLOCKS[block.type].model;
        this.blocks.push(new ModelClass(block, this));
      } else {
        console.warn(`block type "${block.type}" not found. Skipping.`);
      }
    }

    this.block_types = block_types;
    this.default_block_type = default_block_type;
  }

  async add_block(block_type, props = {}, { position } = {}) {
    const block = new BLOCKS[block_type].model(props, this);

    // Inherit layout properties from the currently selected block
    const predecessor_block = this.blocks[this.selected_index];

    if (predecessor_block) {
      block.color = predecessor_block.color;
      if(block.type === 'story') {
        // NOTE 1: we don't want to inherit the layout from the predecessor block, as that is surprising in a lot of situatuations.
        // I think layout 26, is a good default for new stories, as it will usually align with the menu width

        // NOTE 2 (resolved): sites can have several menu widths, so no single layout id
        // lines up with all of them. Width is now a block property, so we keep one default
        // layout and give it the site's width instead - see site_default_track below.
        // NOTE 3: Depending on the type of layout we should inherit the layout from the predecessor block or use the fallback.
        // This should be defined in the StoryLayouts.js "story_layouts" object.
        // Certain layouts are meant to be used only once, others are meant to be used in repetion, and others again are somewhere inbetween
        // (we should test each and see what's the least surprising).
        block.layout = 26; // predecessor_block.layout;
        block.show_description = true;
      }
    }

    // New story blocks adopt the site's width so a page stays coherent by default.
    // Only stamped at creation - applying it as a render-time fallback would silently
    // resize every existing block on every existing page.
    if (block.type === 'story' && !block.track && this.site_default_track) {
      block.track = this.site_default_track;
    }

    const new_blocks = [...this.blocks];
    let insertion_position;

    // We compute the insert position
    if (position >= -1 && position <= this.blocks.length) {
      if (position === -1) {
        insertion_position = this.blocks.length;
      } else if (position >= 0) {
        insertion_position = position;
      } else {
        throw new Error('Invalid position provided');
      }
    } else {
      // If no position provided, insert after selected block, when available, or at the very end.
      insertion_position = this.selected_index >= 0 ? this.selected_index + 1 : this.blocks.length;
    }

    new_blocks.splice(
      insertion_position,
      0,
      block,
    );
    this.blocks = new_blocks; // trigger update
    
    await tick(); // we need to wait for the new block to be rendered and linked to the component

    this.ref?.focus_block(insertion_position, 'start');

    return insertion_position;
  }

  // TODO: Rename to delete_selected_blocks
  delete_block() {
    const new_blocks = [...this.blocks];
    let next_index;
  
    if (this.selection.type === 'container') {
      // We need to remove the blocks in reverse order so indexes don't shift
      const sorted_indexes = [...this.selection.block_indexes].sort((a, b) => b - a);
      for (const block_index of sorted_indexes) {
        const [removed] = new_blocks.splice(block_index, 1);
        removed?.dispose?.();
      }
      next_index = Math.min(sorted_indexes[sorted_indexes.length - 1], new_blocks.length - 1);
    } else if (this.selection.type === 'field') {
      const [removed] = new_blocks.splice(this.selection.block_index, 1);
      removed?.dispose?.();
      next_index = Math.min(this.selection.block_index, new_blocks.length - 1);
    }
  
    this.blocks = new_blocks; // trigger update
  
    // Select the next block, if available, for faster editing experience
    // It's more common to stay in the same selection mode than to e.g. change to text selection, 
    // and even that is more common than no selection at all.
    if (next_index >= 0 && next_index < this.blocks.length) {
      this.set_selection({
        type: 'container',
        block_indexes: [next_index],
        block_anchor_index: next_index,
      });
      this.ref?.focus_block(next_index, 'start');
    } else {
      this.set_selection(undefined);
    }
  }
  move_block(direction) {
    if (!this.selection) return; // No selection, nothing to move

    let start_index;
    let num_selected_blocks;
    const current_selection_type = this.selection.type;

    if (current_selection_type === 'container') {
      if (!this.is_continous_selection()) {
        console.log('Move operation only possible for continuous container selections. Skipping...');
        return;
      }
      start_index = this.selection.block_indexes[0];
      num_selected_blocks = this.selection.block_indexes.length;
    } else if (current_selection_type === 'field') {
      start_index = this.selection.block_index;
      num_selected_blocks = 1;
    } else {
      console.warn('Unknown selection type:', this.selection.type);
      return;
    }

    const new_blocks = [...this.blocks];
    const blocks_to_move = new_blocks.splice(start_index, num_selected_blocks);
    const to_index = direction === 'up' ? start_index - 1 : start_index + 1;

    if (to_index < 0 || (to_index + num_selected_blocks) > this.blocks.length) {
      console.info('Cannot shift blocks out of bounds. Skipping.');
      // Restore the original blocks array since splice modified it
      this.blocks = [...this.blocks];
      return;
    }
    
    new_blocks.splice(to_index, 0, ...blocks_to_move);
    
    this.blocks = new_blocks; // trigger update
    
    // Update selection to reflect the move
    const index_shift = direction === 'up' ? -1 : 1;
    let new_selection;

    if (current_selection_type === 'container') {
      new_selection = {
        type: 'container',
        block_indexes: this.selection.block_indexes.map(i => i + index_shift),
        block_anchor_index: this.selection.block_anchor_index + index_shift,
      };
      this.ref?.scroll_block_into_view(new_selection.block_indexes[0]);
    } else if (current_selection_type === 'field') {
      new_selection = {
        ...this.selection, // Keep field name, char indexes etc.
        block_index: this.selection.block_index + index_shift,
      };
      this.ref?.scroll_block_into_view(new_selection.block_index);
    }

    this.set_selection(new_selection);

  }

  duplicate_selected_blocks() {
    if (!this.selection) return; // No selection, nothing to duplicate

    let start_index;
    let num_blocks_to_duplicate;
    let blocks_to_duplicate_data = [];
    const current_selection_type = this.selection.type;

    if (current_selection_type === 'container') {
      if (!this.is_continous_selection()) {
        console.log('Duplicate operation only possible for continuous container selections. Skipping...');
        return;
      }
      start_index = this.selection.block_indexes[0];
      num_blocks_to_duplicate = this.selection.block_indexes.length;
      blocks_to_duplicate_data = this.blocks.slice(start_index, start_index + num_blocks_to_duplicate);
    } else if (current_selection_type === 'field') {
      start_index = this.selection.block_index;
      num_blocks_to_duplicate = 1;
      blocks_to_duplicate_data = [this.blocks[start_index]];
    } else {
      console.warn('Unknown selection type:', this.selection.type);
      return;
    }

    // TODO: remove the contents of hidden fields, only copy visible fields:
    // Reason: it's surprising to have random text appear when toggling the visibility of a field.
    const { blocks } = prepare_blocks_for_insert(
      blocks_to_duplicate_data.map(block => block.to_json()),
      { same_entry: true }, // the originals stay right above, so in-page anchors still resolve
    );

    // Insert the duplicated blocks right after the original selection
    this.insert_blocks(blocks, start_index + num_blocks_to_duplicate);
  }

  /**
   * The selected blocks as plain JSON, in document order - ready for the clipboard.
   * Returns null for a text selection (the browser's own copy has to win there) and for an
   * empty one. Unlike move and duplicate this also handles fragmented selections, since only
   * the order of the copied blocks matters.
   */
  copy_selected_blocks() {
    if (!this.is_container_selection()) return null;
    const blocks = [...this.selection.block_indexes]
      .sort((a, b) => a - b)
      .map(index => this.blocks[index])
      .filter(Boolean)
      .map(block => block.to_json());
    return blocks.length > 0 ? blocks : null;
  }

  /**
   * Paste blocks from the clipboard after the selection, or at the end when nothing is selected.
   * @param {object[]} blocks_json
   * @param {{ same_entry?: boolean }} [options] - true when they were copied from this entry
   * @returns {number} how many in-page anchors could not follow the blocks to this page
   */
  paste_blocks(blocks_json, { same_entry = false } = {}) {
    const { blocks, dropped_anchor_count } = prepare_blocks_for_insert(blocks_json, { same_entry });

    const selected_indexes = this.selection?.block_indexes
      ?? (this.selection?.block_index >= 0 ? [this.selection.block_index] : null);
    const insertion_index = selected_indexes ? Math.max(...selected_indexes) + 1 : this.blocks.length;

    this.insert_blocks(blocks, insertion_index);
    return dropped_anchor_count;
  }

  /**
   * Insert blocks given as plain JSON at `index` and select them. Shared by duplicate (same page)
   * and paste (any page) - both are "turn this JSON into fresh models and drop them in".
   * The JSON must already carry unique block_ids, see `prepare_blocks_for_insert()`.
   */
  insert_blocks(blocks_json, index) {
    const new_block_models = blocks_json
      .filter(block => {
        if (BLOCKS[block?.type]) return true;
        console.warn(`block type "${block?.type}" not found. Skipping.`);
        return false;
      })
      .map(block => new BLOCKS[block.type].model(block, this));

    if (new_block_models.length === 0) return;

    const insertion_index = Math.min(Math.max(index ?? this.blocks.length, 0), this.blocks.length);
    const new_blocks = [...this.blocks];
    new_blocks.splice(insertion_index, 0, ...new_block_models);
    this.blocks = new_blocks; // trigger update

    // Select the newly inserted block(s) as a container selection
    const new_selected_indexes = new_block_models.map((_, i) => insertion_index + i);
    this.set_selection({
      type: 'container',
      block_indexes: new_selected_indexes,
      block_anchor_index: new_selected_indexes[0], // Anchor at the first inserted block
    });

    this.ref?.scroll_block_into_view(new_selected_indexes[0]);
  }

  // [1,2,3] -> [4]
  select_next_block() {
    if (this.selection.type !== 'container') return; // only works for container selections
    let next_index = Math.min(Math.max(...this.selection.block_indexes) + 1, this.blocks.length - 1);
    this.set_selection({
      type: 'container',
      block_indexes: [next_index],
      block_anchor_index: next_index,
    });
    this.ref?.scroll_block_into_view(this.selected_index);
  }

  // [3,4,5] -> [2]
  select_previous_block() {
    if (this.selection.type !== 'container') return; // only works for container selections
    let prev_index = Math.max(Math.min(...this.selection.block_indexes) - 1, 0);
    this.set_selection({
      type: 'container',
      block_indexes: [prev_index],
      block_anchor_index: prev_index,
    });
    this.ref?.scroll_block_into_view(this.selected_index);
  }

  // If silent is true, we don't update the UI
  set_selection(selection, silent) {
    this.selection = selection;
    // TODO: check if given selection is valid, if not warn and skip.
    if (!silent) {
      // this.ref?.update_dom_selection();
    }
  }

  // Returns true if the given block index is within the selection
  is_index_selected(index) {
    if (!this.selection) return false;
    if (this.selection.type === 'container') {
      return this.selection.block_indexes.includes(index);
    } else {
      return this.selection.block_index === index;
    }
  }

  // Unlike expand_selection, this allows creating a fragmented selection
  // Naming is not great yet.
  toggle_block_selection(index) {
    if (this.selection.type !== 'container') return;
    const selected_block_indexes = [...this.selection.block_indexes];
    if (selected_block_indexes.includes(index)) {
      // Untoggle if already selected
      const idx = selected_block_indexes.indexOf(index);
      selected_block_indexes.splice(idx, 1);
      if (selected_block_indexes.length > 0) {
        this.selection.block_indexes = selected_block_indexes;
      } else {
        this.selection = undefined;
      }
    } else {
      selected_block_indexes.push(index);
      selected_block_indexes.sort((a, b) => a - b);
      this.selection.block_indexes = selected_block_indexes;
    }
  }

  // We can expand the selection relative to the anchor block
  expand_selection(direction) {
    if (this.selection.type !== 'container') return;
    if (!this.is_continous_selection()) {
      console.info('expanding selections only works for continous selections atm.');
      return;
    }

    const start_index = this.selection.block_indexes[0];
    const anchor_index = this.selection.block_anchor_index;
    const is_upwards_from_anchor = start_index < anchor_index;
    let focus_index; // used to scroll the focused end of the selection into view

    // if (!(this.selected_index >= 0)) return;
    if (direction === 'down') {
      if (is_upwards_from_anchor) {
        // remove the first element from the selection
        this.selection.block_indexes.shift();
        focus_index = this.selection.block_indexes.at(0); // first selected block should get the focus
      } else {
        // add the next element to the selection
        const next_index = Math.max(...this.selection.block_indexes) + 1;
        if (next_index < this.blocks.length) { // prevent out of bound situation
          this.selection.block_indexes = [...this.selection.block_indexes, next_index];
        }
        focus_index = this.selection.block_indexes.at(-1); // last selected block should get the focus
      }
    } else if (direction === 'up') {
      if (is_upwards_from_anchor || this.selection.block_indexes.length === 1) {
        const previous_index = Math.min(...this.selection.block_indexes) - 1;
        if (previous_index >= 0) { // prevent out of bound situation
          this.selection.block_indexes = [previous_index, ...this.selection.block_indexes];
        }
        focus_index = this.selection.block_indexes.at(0); // first selected block should get the focus
      } else {
        // Remove the last element from the selection
        this.selection.block_indexes.pop();
        focus_index = this.selection.block_indexes.at(-1); // last selected block should get the focus
      }
    }

    this.ref.scroll_block_into_view(focus_index);
  }

  clear_selection() {
    this.set_selection(undefined);
  }

  // [3,4,5,6] -> true, [3, 5, 6] -> false
  is_continous_selection() {
    if (!this.selection) return false;
    if (this.selection.type === 'field') return true;
    const arr = this.selection.block_indexes;
    // Check if all subsequent elements have the same difference
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] - arr[i - 1] !== 1) {
          return false;
      }
    }
    return true;
  }

  is_container_selection() {
    return this.selection?.type === 'container';
  }

  is_field_selection() {
    return this.selection?.type === 'field';
  }

  /**
   * Dispose all blocks that support cleanup (e.g. StoryBlockModel's $effect.root).
   * Call before discarding this container to prevent leaked effects.
   */
  dispose() {
    for (const block of this.blocks) {
      block.dispose?.();
    }
  }

  to_json() {
    const result = [];
    this.blocks.forEach((block) => {
      result.push(block.to_json());
    });
    return result;
  }

  toJSON() {
    return this.to_json();
  }
}
